import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Row, Col, ListGroup, CloseButton, Accordion } from 'react-bootstrap';
import { createSurvey } from '../services/api';

const CreateSurvey = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [groups, setGroups] = useState([]);
  const [generalQuestions, setGeneralQuestions] = useState([]);
  
  // New group form state
  const [newGroupName, setNewGroupName] = useState('');
  
  // New question form state
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'text',
    notes: '',
    options: '',
    groupIndex: -1 // -1 for general questions, 0+ for group index
  });

  const addGroup = () => {
    if (!newGroupName.trim()) return;
    
    setGroups([...groups, {
      name: newGroupName,
      questions: []
    }]);
    
    setNewGroupName('');
  };

  const removeGroup = (groupIndex) => {
    const updatedGroups = [...groups];
    updatedGroups.splice(groupIndex, 1);
    setGroups(updatedGroups);
  };

  const addQuestion = () => {
    if (!newQuestion.text.trim()) return;
    
    const questionObj = {
      text: newQuestion.text,
      type: newQuestion.type,
      notes: newQuestion.notes,
      options: newQuestion.type !== 'text' ? newQuestion.options.split(',').map(opt => opt.trim()).filter(opt => opt) : []
    };
    
    if (newQuestion.groupIndex === -1) {
      // Add to general questions
      setGeneralQuestions([...generalQuestions, questionObj]);
    } else {
      // Add to specific group
      const updatedGroups = [...groups];
      updatedGroups[newQuestion.groupIndex].questions.push(questionObj);
      setGroups(updatedGroups);
    }
    
    // Reset form
    setNewQuestion({
      text: '',
      type: 'text',
      notes: '',
      options: '',
      groupIndex: newQuestion.groupIndex
    });
  };

  const removeQuestion = (questionIndex, groupIndex = -1) => {
    if (groupIndex === -1) {
      // Remove from general questions
      const updatedQuestions = [...generalQuestions];
      updatedQuestions.splice(questionIndex, 1);
      setGeneralQuestions(updatedQuestions);
    } else {
      // Remove from specific group
      const updatedGroups = [...groups];
      updatedGroups[groupIndex].questions.splice(questionIndex, 1);
      setGroups(updatedGroups);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Survey title is required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const surveyData = {
        title,
        description,
        groups,
        general_questions: generalQuestions
      };
      
      const response = await createSurvey(surveyData);
      navigate(`/surveys/${response.id}`);
    } catch (err) {
      console.error('Error creating survey:', err);
      setError('Failed to create survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="mb-4">Create New Survey</h1>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Survey Details</h2>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter survey title"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter survey description (optional)"
              />
            </Form.Group>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Question Groups</h2>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Add New Group</Form.Label>
              <div className="d-flex">
                <Form.Control
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name (e.g., Personal Information)"
                  className="me-2"
                />
                <Button variant="outline-primary" onClick={addGroup}>
                  Add Group
                </Button>
              </div>
            </Form.Group>
            
            {groups.length > 0 && (
              <Accordion className="mb-4">
                {groups.map((group, index) => (
                  <Accordion.Item key={index} eventKey={index.toString()}>
                    <Accordion.Header className="d-flex">
                      <div className="d-flex justify-content-between w-100 me-3">
                        <span>{group.name}</span>
                        <span className="badge bg-secondary">{group.questions.length} questions</span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="mb-2 d-flex justify-content-between">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setNewQuestion({...newQuestion, groupIndex: index})}
                        >
                          Add Question to This Group
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => removeGroup(index)}
                        >
                          Remove Group
                        </Button>
                      </div>
                      
                      {group.questions.length > 0 ? (
                        <ListGroup variant="flush">
                          {group.questions.map((question, qIndex) => (
                            <ListGroup.Item key={qIndex} className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="mb-1 fw-bold">
                                  <span className="badge bg-primary me-2">{question.type}</span>
                                  {question.text}
                                </div>
                                {question.notes && (
                                  <div className="small text-muted">Note: {question.notes}</div>
                                )}
                                {question.options && question.options.length > 0 && (
                                  <div className="small">Options: {question.options.join(", ")}</div>
                                )}
                              </div>
                              <CloseButton onClick={() => removeQuestion(qIndex, index)} />
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="text-center py-3 text-muted">
                          No questions in this group yet
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">Questions</h2>
            {generalQuestions.length > 0 && (
              <span className="badge bg-secondary">{generalQuestions.length} general questions</span>
            )}
          </Card.Header>
          <Card.Body>
            <Card className="mb-4">
              <Card.Header>
                <h3 className="h6 mb-0">Add New Question</h3>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Question Text *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newQuestion.text}
                    onChange={(e) => setNewQuestion({...newQuestion, text: e.target.value})}
                    placeholder="Enter question text"
                  />
                </Form.Group>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Question Type</Form.Label>
                      <Form.Select
                        value={newQuestion.type}
                        onChange={(e) => setNewQuestion({...newQuestion, type: e.target.value})}
                      >
                        <option value="text">Text</option>
                        <option value="select">Select (Multi-select)</option>
                        <option value="checkbox">Checkbox (Multi-select)</option>
                        <option value="radio">Radio (Single choice)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Add to Group</Form.Label>
                      <Form.Select
                        value={newQuestion.groupIndex}
                        onChange={(e) => setNewQuestion({...newQuestion, groupIndex: parseInt(e.target.value)})}
                      >
                        <option value={-1}>General Questions (No Group)</option>
                        {groups.map((group, index) => (
                          <option key={index} value={index}>{group.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                {newQuestion.type !== 'text' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Options (comma-separated)</Form.Label>
                    <Form.Control
                      type="text"
                      value={newQuestion.options}
                      onChange={(e) => setNewQuestion({...newQuestion, options: e.target.value})}
                      placeholder="Enter options separated by commas (e.g., Yes, No, Maybe)"
                    />
                    <Form.Text className="text-muted">
                      For multiple choice questions, enter options separated by commas
                    </Form.Text>
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label>Notes (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={newQuestion.notes}
                    onChange={(e) => setNewQuestion({...newQuestion, notes: e.target.value})}
                    placeholder="Additional information about this question"
                  />
                </Form.Group>
                
                <div className="text-end">
                  <Button variant="primary" onClick={addQuestion}>
                    Add Question
                  </Button>
                </div>
              </Card.Body>
            </Card>
            
            {generalQuestions.length > 0 && (
              <div>
                <h3 className="h6 mb-3">General Questions</h3>
                <ListGroup variant="flush">
                  {generalQuestions.map((question, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="mb-1 fw-bold">
                          <span className="badge bg-primary me-2">{question.type}</span>
                          {question.text}
                        </div>
                        {question.notes && (
                          <div className="small text-muted">Note: {question.notes}</div>
                        )}
                        {question.options && question.options.length > 0 && (
                          <div className="small">Options: {question.options.join(", ")}</div>
                        )}
                      </div>
                      <CloseButton onClick={() => removeQuestion(index)} />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Creating Survey...' : 'Create Survey'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateSurvey;